import { useEffect } from 'react';

import { useFormContext } from 'react-hook-form';

import { useAppSelector } from 'src/app/store/hooks';
import {
  PayerOrganizationRead,
  usePayersPassengersRetrieveQuery,
} from 'src/common/external/bambi-api/bambiApi';
import { FormField } from 'src/common/FormField';
import { FormFieldController } from 'src/common/FormField/FormFieldController';

import { useIsEditingTrip } from '../useIsEditingTrip';
import { usePayerTripRequiredField } from '../usePayerTripRequiredField';
import { pricingNameRoot } from './pricingNameRoot';

export function InsurancePayerFields({
  selectedPayer,
}: {
  selectedPayer: PayerOrganizationRead | null;
}) {
  const isAuthorizationIdRequired = usePayerTripRequiredField(
    'authorization_id',
    selectedPayer
  );

  const isInsurancePolicyIdRequired = usePayerTripRequiredField(
    'insurance_policy_id',
    selectedPayer
  );

  const { setValue } = useFormContext();
  const isEditingTrip = useIsEditingTrip();

  const selectedPassenger = useAppSelector(
    (state) => state.addATrip.selectedPassenger
  );
  const selectedTrip = useAppSelector((state) => state.addATrip.selectedTrip);

  const { currentData: payerPassenger } = usePayersPassengersRetrieveQuery(
    {
      passengerId: selectedPassenger?.id || '',
      payerOrganizationId: selectedPayer?.id || '',
    },
    {
      skip:
        !selectedPassenger?.id ||
        !selectedPayer?.id ||
        selectedPayer.payer_type !== 'insurance',
    }
  );

  const hasPassengerOrPayerChangedFromSavedTrip =
    isEditingTrip &&
    (selectedPassenger?.id !== selectedTrip?.passenger.id ||
      selectedPayer?.id !== selectedTrip?.payer.id);

  useEffect(() => {
    if (!payerPassenger?.id) return;
    if (isEditingTrip && !hasPassengerOrPayerChangedFromSavedTrip) return;

    setValue(
      `${pricingNameRoot}.insurance_policy_id`,
      payerPassenger.external_id ?? ''
    );
    setValue(
      `${pricingNameRoot}.authorization_id`,
      payerPassenger.authorization_id ?? ''
    );
  }, [
    payerPassenger,
    isEditingTrip,
    hasPassengerOrPayerChangedFromSavedTrip,
    setValue,
  ]);

  return (
    <>
      <div className="grow" data-testid="insurance-payer-fields">
        <FormFieldController
          name={`${pricingNameRoot}.authorization_id`}
          rules={{
            required: isAuthorizationIdRequired
              ? 'Authorization ID is required'
              : undefined,
          }}
        >
          {({ field, fieldState }) => {
            return (
              <FormField
                label="Authorization ID/Number"
                error={fieldState.error?.message?.toString()}
                type="text"
                inputProps={{
                  id: `${pricingNameRoot}.authorization_id`,
                  ...field,
                }}
              />
            );
          }}
        </FormFieldController>
      </div>
      <div className="grow">
        <FormFieldController
          name={`${pricingNameRoot}.insurance_policy_id`}
          rules={{
            required: isInsurancePolicyIdRequired
              ? 'Insurance Policy ID is required'
              : undefined,
          }}
        >
          {({ field, fieldState }) => {
            return (
              <FormField
                label="Insurance Policy ID"
                error={fieldState.error?.message?.toString()}
                type="text"
                inputProps={{
                  id: `${pricingNameRoot}.insurance_policy_id`,
                  ...field,
                }}
              />
            );
          }}
        </FormFieldController>
      </div>
    </>
  );
}
